import React from 'react'
import styled from 'styled-components'
import tw, { theme } from 'twin.macro'
import { useTranslation } from 'react-i18next'
import ExpandingTextarea from 'react-expanding-textarea'
import { useInputState } from '@hooks/useInputState'
import { OG_TITLE_MAX_LENGTH, OG_DESCRIPTION_MAX_LENGTH } from '../_shared'
import { QuestionIcon } from '@components/Icons'

type SocialMediaTextSettingProps = {
  updateOgTitleChange?: (value: string) => void
  updateOgDescriptionChange?: (value: string) => void
  defaultOgTitle?: string
  initialOgTitle?: string
  defaultOgDescription?: string
  initialOgDescription?: string
}

const OG_TITLE_FIELD_NAME = 'og-title'
const OG_DESCRIPTION_FIELD_NAME = 'og-description'

export default function SocialMediaTextSettingView(props: SocialMediaTextSettingProps) {
  const {
    updateOgTitleChange,
    defaultOgTitle = '',
    initialOgTitle,
    updateOgDescriptionChange,
    defaultOgDescription = '',
    initialOgDescription,
  } = props
  const { t } = useTranslation('editor')

  const [
    [ogTitle, ogDescription],
    handleInputChange,
    handleInputComposingUpdate,
    handleInputFocusChange,
  ] = useInputState([
    {
      id: OG_TITLE_FIELD_NAME,
      defaultValue: defaultOgTitle,
      initialValue: initialOgTitle,
      onChange: updateOgTitleChange,
    },
    {
      id: OG_DESCRIPTION_FIELD_NAME,
      defaultValue: defaultOgDescription,
      initialValue: initialOgDescription,
      onChange: updateOgDescriptionChange,
    },
  ])

  const toggleHintVisibility = (e: React.MouseEvent<SVGSVGElement>) => {
    const hintContainer = e.currentTarget.closest('.hint-container')
    const hint = hintContainer.querySelector('.hint') as HTMLElement
    const currentVisibility = hint.style.display && hint.style.display !== 'none'
    hint.style.display = currentVisibility ? 'none' : 'flex'
  }

  return (
    <div tw="space-y-4">
      <div tw="space-y-2">
        <div tw="flex items-center">
          <StyledLabel htmlFor={OG_TITLE_FIELD_NAME}>
            {t(['socialMediaTitleInput.heading', '社群封面標題'])}
          </StyledLabel>
          <HintContainer className="hint-container">
            <StyledQuestionIcon onClick={(e) => toggleHintVisibility(e)} />
            <Hint className="hint">
              <ArrowUp />
              <span>
                {t([
                  'socialMediaTitleInput.hint',
                  '這段文字會顯示於社群上文章預覽的標題，正確、合理的文案表達可以協助你的文章在 Facebook 等第三方社群吸引讀者的點擊。',
                ])}
              </span>
            </Hint>
          </HintContainer>
        </div>
        <StyledInput
          id={OG_TITLE_FIELD_NAME}
          maxLength={OG_TITLE_MAX_LENGTH}
          placeholder={t(['socialMediaTitleInput.placeholder', '請輸入標題'])}
          value={ogTitle.value}
          data-status={ogTitle.status}
          onInput={handleInputChange}
          onCompositionStart={handleInputComposingUpdate}
          onCompositionEnd={handleInputComposingUpdate}
          onFocus={handleInputFocusChange}
          onBlur={handleInputFocusChange}
        />
        <Counter
          css={[
            ogTitle.status === 'empty' && tw`hidden`,
            ogTitle.status === 'maximum' && tw`text-secondary-close`,
          ]}
        >
          {ogTitle.count} / {OG_TITLE_MAX_LENGTH}
        </Counter>
      </div>
      <div tw="space-y-2">
        <div tw="flex items-center">
          <StyledLabel htmlFor={OG_DESCRIPTION_FIELD_NAME}>
            {t(['socialMediaDescriptionInput.heading', '社群封面描述'])}
          </StyledLabel>
          <HintContainer className="hint-container">
            <StyledQuestionIcon onClick={(e) => toggleHintVisibility(e)} />
            <Hint className="hint">
              <ArrowUp />
              <span>
                {t([
                  'socialMediaDescriptionInput.hint',
                  '這段文字會顯示於社群上文章預覽的說明，好的說明能讓讀者了解你的內容，讓你的文章更容易在 Facebook 等第三方社群吸引注意。',
                ])}
              </span>
            </Hint>
          </HintContainer>
        </div>
        <StyledInput
          id={OG_DESCRIPTION_FIELD_NAME}
          maxLength={OG_DESCRIPTION_MAX_LENGTH}
          placeholder={t(['socialMediaDescriptionInput.placeholder', '請輸入內容'])}
          value={ogDescription.value}
          data-status={ogDescription.status}
          onInput={handleInputChange}
          onCompositionStart={handleInputComposingUpdate}
          onCompositionEnd={handleInputComposingUpdate}
          onFocus={handleInputFocusChange}
          onBlur={handleInputFocusChange}
        />
        <Counter
          css={[
            ogDescription.status === 'empty' && tw`hidden`,
            ogDescription.status === 'maximum' && tw`text-secondary-close`,
          ]}
        >
          {ogDescription.count} / {OG_DESCRIPTION_MAX_LENGTH}
        </Counter>
      </div>
    </div>
  )
}

const StyledLabel = styled.label`
  ${tw`text-gray-800 text-h3-d leading-h1-d`}
  margin-right: 8px;
`

const HintContainer = styled.div`
  ${tw`relative`}
`

const StyledQuestionIcon = styled(QuestionIcon)`
  ${tw`cursor-pointer`}
  width: 16px;
  height: 16px;
  path {
    fill: #333333;
  }
`

const Hint = styled.div`
  ${tw`flex flex-col items-center`}
  ${tw`absolute hidden`}
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: 270px;
  height: 64px;
  span {
    ${tw`bg-gray-800 text-white text-c2-d leading-h1-d rounded-default`}
    ${tw`w-full h-full`}
    padding: 8px 11px;
  }
`

const ArrowUp = styled.div`
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 7px solid #333333;
`

const StyledInput = styled(ExpandingTextarea)`
  ${tw`border-0 outline-none resize-none block w-full`}
  ${tw`text-h5-d leading-h2-d`}
  caret-color: ${theme`colors.primary.indigo`};
  ${tw`pl-1 pb-2 border-b border-gray-100`}

  &::placeholder, &[data-status="empty"] {
    ${tw`text-gray-400`}
  }

  &[data-status='focus'],
  &[data-status='ideal'] {
    ${tw`text-black`}
  }
`

const Counter = styled.span`
  ${tw`text-gray-400 text-c1-d leading-c1-d tracking-tight`}
  ${tw`ml-1`}
`
